import { Coordinate } from "@fairfleet/geotab";
import { useEffect, useMemo, useRef } from "react";
import { useAddresses } from "@/hooks/addresses/useAddresses";

/** A row that may have happened somewhere. */
export interface LocatedRow {
  location: Coordinate | null;
}

/** Identifies a position, so the same depot is only ever looked up once. */
export function coordinateKey(location: Coordinate): string {
  return `${location.y.toFixed(5)},${location.x.toFixed(5)}`;
}

/**
 * Looks up the addresses for the positions of a set of events.
 *
 * Two things shape this. Positions repeat — a fleet charges at a handful of depots and refuels at
 * a handful of stations — so the lookup is keyed by position rather than by row. And the set of
 * positions grows for minutes while the fleet is read, one vehicle at a time, so asking about the
 * whole set again on each arrival would re-run a fleet-wide reverse geocode dozens of times and,
 * because the previous answer belongs to the previous set, leave the address column falling back
 * to bare coordinates in between. Only the positions not yet resolved are asked about, and what
 * has been resolved is kept.
 *
 * @param rows - The rows to look up positions for.
 * @param enabled - Pass `false` for a view nobody is looking at; nothing is requested then.
 * @returns A lookup from {@link coordinateKey} to the formatted address, and the error of the
 * lookup if it failed — without it a failed lookup looks like "no address found".
 */
export function useEventAddresses(
  rows: LocatedRow[],
  enabled = true,
): { addresses: Map<string, string>; error: Error | null } {
  const resolved = useRef(new Map<string, string>());

  const pending = useMemo(() => {
    if (!enabled) {
      return [];
    }
    const unique = new Map<string, Coordinate>();
    for (const row of rows) {
      if (row.location) {
        const key = coordinateKey(row.location);
        if (!resolved.current.has(key)) {
          unique.set(key, row.location);
        }
      }
    }
    return [...unique.values()];
  }, [rows, enabled]);

  const { data, isPlaceholderData, error } = useAddresses(pending);

  const answered = useMemo(() => {
    const found = new Map<string, string>();
    // While a round is in flight the previous answer is still served, and it belongs to the
    // previous set of positions. Pairing it with these by index would file a charging stop under
    // the last station a different vehicle refuelled at — and keep it, since answers are kept.
    if (!data || isPlaceholderData) {
      return found;
    }
    // Paired by order, which is all the response allows: it carries no coordinate of its own.
    pending.forEach((coordinate, index) => {
      const formatted = data[index]?.formattedAddress;
      if (formatted) {
        found.set(coordinateKey(coordinate), formatted);
      }
    });
    return found;
  }, [pending, data, isPlaceholderData]);

  useEffect(() => {
    for (const [key, address] of answered) {
      resolved.current.set(key, address);
    }
  }, [answered]);

  // The answers to this round are merged in directly rather than waited for: the effect above
  // runs after the render, and without the merge the column would show coordinates for a frame.
  const addresses = useMemo(() => new Map([...resolved.current, ...answered]), [answered]);
  return { addresses, error };
}

/**
 * The address of a position, falling back to the coordinates themselves while the lookup is out
 * or when it came back empty.
 *
 * @param location - The position, if there is one.
 * @param addresses - The lookup from {@link useEventAddresses}.
 * @param missing - What to show when the event has no position at all.
 */
export function formatLocation(
  location: Coordinate | null,
  addresses: Map<string, string>,
  missing = "---",
): string {
  if (!location) {
    return missing;
  }
  return addresses.get(coordinateKey(location)) ?? coordinateKey(location);
}
